import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Users,
  Loader2,
  AlertCircle,
  Crown,
  Calendar,
  Trash2,
  CheckCircle,
  XCircle,
  ChevronDown,
  ChevronUp,
  Key,
  Shield,
  ShieldOff,
  Clock,
  Search,
  UserX,
  UserCheck,
  RotateCcw,
  Download,
  Mail,
  Activity,
  RefreshCw
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAdmin } from '../../hooks/useAdmin';
import { AnnouncementsManager } from './AnnouncementsManager';
import { AdsManager } from './AdsManager';
import { SeriesManagerTab } from './SeriesManager/SeriesManagerTab';

interface User {
  id: string;
  email: string;
  created_at: string;
  first_name: string;
  last_name: string;
  phone: string;
  subscription_active: boolean;
  subscription_expires_at: string | null;
  subscription_tier_name: string;
  sermon_count: number;
}

interface Lead {
  id: string;
  email: string;
  first_name: string | null;
  source: string;
  converted: boolean;
  created_at: string;
}

export function AdminDashboard() {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedUser, setSelectedUser] = useState<string | null>(null);
  const { isAdmin, isSuperAdmin, manageSubscription, deleteUser, endTrial, revokeSubscription, resetToTrial, refreshStatus } = useAdmin();
  const [adminUsers, setAdminUsers] = useState<Set<string>>(new Set());
  const [superAdminUsers, setSuperAdminUsers] = useState<Set<string>>(new Set());
  const [actionInProgress, setActionInProgress] = useState<string | null>(null);
  const [trialDays, setTrialDays] = useState<number>(14);
  const [showTrialInput, setShowTrialInput] = useState<string | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState<number>(0);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'trial' | 'premium' | 'expired'>('all');
  const [activeTab, setActiveTab] = useState<'users' | 'leads' | 'ai_usage' | 'content' | 'series'>('users');
  const [leads, setLeads] = useState<Lead[]>([]);
  const [leadsLoading, setLeadsLoading] = useState(false);

  interface AIUsageRow {
    user_id: string;
    email: string;
    first_name: string | null;
    last_name: string | null;
    total_requests: number;
    openai_requests: number;
    anthropic_requests: number;
    last_request_at: string;
  }
  interface AIBreakdownRow { request_type: string; request_count: number; }

  const [aiUsage, setAiUsage] = useState<AIUsageRow[]>([]);
  const [aiUsageLoading, setAiUsageLoading] = useState(false);
  const [aiDaysBack, setAiDaysBack] = useState(30);
  const [expandedAIUser, setExpandedAIUser] = useState<string | null>(null);
  const [aiBreakdown, setAiBreakdown] = useState<Record<string, AIBreakdownRow[]>>({});

  useEffect(() => {
    if (!isAdmin) {
      setError('Unauthorized: Admin access required');
      return;
    }
    loadUsers();
    loadLeads();
  }, [isAdmin, refreshTrigger]);

  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const { data: usersData, error: usersError } = await supabase.rpc('get_admin_users');
      if (usersError) throw usersError;

      // Get admin users
      const { data: adminData, error: adminError } = await supabase
        .from('admin_users')
        .select('user_id, role');

      if (adminError) throw adminError;

      // Update admin sets
      const admins = new Set<string>();
      const superAdmins = new Set<string>();
      adminData?.forEach(admin => {
        if (admin.role === 'super_admin') {
          superAdmins.add(admin.user_id);
        } else {
          admins.add(admin.user_id);
        }
      });

      setAdminUsers(admins);
      setSuperAdminUsers(superAdmins);
      setUsers(usersData || []);
    } catch (err) {
      console.error('Error loading users:', err);
      setError(err instanceof Error ? err.message : 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadLeads = useCallback(async () => {
    try {
      setLeadsLoading(true);
      const { data, error: leadsError } = await supabase
        .from('leads')
        .select('*')
        .order('created_at', { ascending: false });

      if (leadsError) throw leadsError;
      setLeads(data || []);
    } catch (err) {
      console.error('Error loading leads:', err);
    } finally {
      setLeadsLoading(false);
    }
  }, []);

  const loadAIUsage = useCallback(async (days: number) => {
    try {
      setAiUsageLoading(true);
      const { data, error: usageError } = await supabase.rpc('get_ai_usage_summary', { days_back: days });
      if (usageError) throw usageError;
      setAiUsage(data || []);
    } catch (err) {
      console.error('Error loading AI usage:', err);
    } finally {
      setAiUsageLoading(false);
    }
  }, []);

  const loadUserAIBreakdown = async (userId: string, days: number) => {
    if (aiBreakdown[userId]) return; // already loaded
    try {
      const { data, error } = await supabase.rpc('get_user_ai_breakdown', {
        target_user_id: userId,
        days_back: days,
      });
      if (error) throw error;
      setAiBreakdown(prev => ({ ...prev, [userId]: data || [] }));
    } catch (err) {
      console.error('Error loading user AI breakdown:', err);
    }
  };

  const downloadLeadsCSV = () => {
    if (leads.length === 0) return;

    const headers = ['Email', 'First Name', 'Source', 'Converted', 'Date'];
    const rows = leads.map(lead => [
      lead.email,
      lead.first_name || '',
      lead.source || 'free-study-guide',
      lead.converted ? 'Yes' : 'No',
      new Date(lead.created_at).toLocaleDateString()
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `study-guide-leads-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleExtendTrial = async (userId: string, days: number) => {
    try {
      setActionInProgress(userId);
      setError(null);
      
      // Call the manage subscription function with trial extension
      await manageSubscription(userId, 'premium', true, days);
      
      // Update the local state immediately to show the change
      const newExpiryDate = new Date(Date.now() + (days * 24 * 60 * 60 * 1000));
      setUsers(prevUsers => 
        prevUsers.map(user => 
          user.id === userId 
            ? {
                ...user,
                subscription_expires_at: newExpiryDate.toISOString(),
                subscription_active: true,
                subscription_tier_name: 'Premium (Trial)'
              }
            : user
        )
      );
      
      setShowTrialInput(null);
      
      // Force a refresh after a short delay to get the latest data from server
      setTimeout(() => {
        setRefreshTrigger(prev => prev + 1);
      }, 1000);
      
    } catch (err) {
      console.error('Error extending trial:', err);
      setError('Failed to extend trial: ' + (err instanceof Error ? err.message : 'Unknown error'));
    } finally {
      setActionInProgress(null);
    }
  };

  const handleChangeSubscription = async (userId: string, tier: string) => {
    try {
      setActionInProgress(userId);
      setError(null);

      // Determine the display name and subscription properties
      const displayTierName = tier === 'free_premium' ? 'Premium (Free)' : 'Premium';
      const isFreePremium = tier === 'free_premium';

      // First update the local state to show immediate feedback
      setUsers(prevUsers => 
        prevUsers.map(user => 
          user.id === userId 
            ? {
                ...user,
                subscription_tier_name: displayTierName,
                subscription_active: true,
                subscription_expires_at: isFreePremium ? null : user.subscription_expires_at
              }
            : user
        )
      );

      // Make the API call
      await manageSubscription(userId, tier, false);

      // Force a refresh of the user list after a short delay
      setTimeout(() => {
        setRefreshTrigger(prev => prev + 1);
      }, 1000);

      // Refresh admin status in case this affected the current user
      await refreshStatus();

    } catch (err) {
      console.error('Error changing subscription:', err);
      setError('Failed to change subscription: ' + (err instanceof Error ? err.message : 'Unknown error'));
      // Revert the local state change on error by forcing a refresh
      setRefreshTrigger(prev => prev + 1);
    } finally {
      setActionInProgress(null);
    }
  };

  const handleDeleteUser = async (id: string, userEmail: string) => {
    const confirmText = `Are you sure you want to permanently delete the user "${userEmail}"?\n\nThis will:\n- Delete all their sermons and data\n- Remove their subscription\n- Cannot be undone\n\nType "DELETE" to confirm:`;

    const confirmation = window.prompt(confirmText);
    if (confirmation !== 'DELETE') {
      return;
    }

    try {
      setActionInProgress(id);
      setError(null);
      await deleteUser(id);
      setRefreshTrigger(prev => prev + 1);
      alert('User successfully deleted');
    } catch (err) {
      console.error('Error deleting user:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete user';
      setError(`Delete failed: ${errorMessage}`);
      alert(`Failed to delete user: ${errorMessage}`);
    } finally {
      setActionInProgress(null);
    }
  };

  const handleResetPassword = async (userId: string, userEmail: string) => {
    try {
      setLoading(true);

      // Use the existing admin_reset_password RPC. NOTE: that SQL function only
      // writes an email_logs row — it does not itself dispatch mail. Pre-existing
      // gap, unrelated to the Resend migration.
      const { data, error } = await supabase.rpc('admin_reset_password', {
        target_user_id: userId,
        send_email: true
      });

      if (error) throw error;

      if (data?.success) {
        alert('Password reset email sent successfully!');
      } else {
        throw new Error(data?.error || 'Failed to send password reset email');
      }
    } catch (error) {
      console.error('Error sending reset email:', error);
      alert('Failed to send password reset email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleAdmin = async (id: string, isAdmin: boolean, isSuperAdmin: boolean = false) => {
    try {
      setActionInProgress(id);
      if (isAdmin) {
        // Revoke admin access
        const { error } = await supabase.rpc('revoke_admin_access', {
          target_user_id: id
        });
        if (error) throw error;
      } else {
        // Grant admin access
        const { error } = await supabase.rpc('grant_admin_access', {
          target_user_id: id,
          admin_role: isSuperAdmin ? 'super_admin' : 'admin'
        });
        if (error) throw error;
      }
      setRefreshTrigger(prev => prev + 1);
    } catch (err) {
      console.error('Error toggling admin access:', err);
      setError('Failed to update admin access');
    } finally {
      setActionInProgress(null);
    }
  };

  const isTrialActive = (user: User) => {
    const userCreatedAt = new Date(user.created_at);
    const trialEnd = new Date(userCreatedAt.getTime() + (14 * 24 * 60 * 60 * 1000));
    const now = new Date();
    return now < trialEnd;
  };

  const isTrialExpired = (user: User) => {
    const userCreatedAt = new Date(user.created_at);
    const trialEnd = new Date(userCreatedAt.getTime() + (14 * 24 * 60 * 60 * 1000));
    const now = new Date();
    return now >= trialEnd && !user.subscription_active;
  };

  const getTrialDaysLeft = (user: User) => {
    const userCreatedAt = new Date(user.created_at);
    const trialEnd = new Date(userCreatedAt.getTime() + (14 * 24 * 60 * 60 * 1000));
    const now = new Date();
    const diffTime = trialEnd.getTime() - now.getTime();
    return Math.max(0, Math.ceil(diffTime / (1000 * 60 * 60 * 24)));
  };

  const getDaysLeft = (expiresAt: string) => {
    const today = new Date();
    const expiration = new Date(expiresAt);
    const diffTime = expiration.getTime() - today.getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  const getUserStatus = (user: User): 'trial' | 'premium' | 'expired' => {
    const tierName = user.subscription_tier_name?.toLowerCase() || '';

    // Admin explicitly ended this user's trial
    if (tierName === 'expired') {
      return 'expired';
    }

    if (user.subscription_active) {
      // Treat as expired if expires_at is set and in the past
      if (user.subscription_expires_at && new Date(user.subscription_expires_at) <= new Date()) {
        return 'expired';
      }
      if (tierName.includes('trial')) {
        return 'trial';
      }
      return 'premium';
    }

    if (isTrialActive(user)) {
      return 'trial';
    }

    return 'expired';
  };

  const getStatusLabel = (user: User): string => {
    const tierName = user.subscription_tier_name || '';
    const status = getUserStatus(user);

    // Show collaborator tier explicitly so admins can identify team invite users
    if (tierName === 'Collaborator') return 'Collaborator';

    if (status === 'premium') {
      if (tierName.includes('Free') || tierName.includes('Gifted')) {
        return 'Premium (Gifted)';
      }
      return 'Premium';
    }
    if (status === 'trial') {
      const daysLeft = getTrialDaysLeft(user);
      return daysLeft > 0 ? `Trial (${daysLeft} days left)` : 'Trial';
    }
    return 'Expired';
  };

  const filteredUsers = useMemo(() => {
    return users.filter(user => {
      const matchesSearch = searchQuery === '' || 
        user.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (user.first_name && user.first_name.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (user.last_name && user.last_name.toLowerCase().includes(searchQuery.toLowerCase()));
      
      if (!matchesSearch) return false;
      
      if (statusFilter === 'all') return true;
      return getUserStatus(user) === statusFilter;
    });
  }, [users, searchQuery, statusFilter]);

  const stats = useMemo(() => {
    const activeTrials = users.filter(u => isTrialActive(u) && getUserStatus(u) !== 'premium').length;
    const premiumUsers = users.filter(u => getUserStatus(u) === 'premium').length;
    const expiredTrials = users.filter(u => isTrialExpired(u) && getUserStatus(u) !== 'premium').length;
    return { activeTrials, premiumUsers, expiredTrials, total: users.length };
  }, [users]);

  const handleEndTrial = async (userId: string) => {
    if (!confirm('Are you sure you want to end this user\'s trial? They will lose access to premium features immediately.')) {
      return;
    }
    
    try {
      setActionInProgress(userId);
      setError(null);
      await endTrial(userId);
      
      setUsers(prevUsers => 
        prevUsers.map(user => 
          user.id === userId 
            ? { ...user, subscription_active: false, subscription_tier_name: 'Expired' }
            : user
        )
      );
      
      setMessage('Trial ended successfully');
      setTimeout(() => setMessage(null), 3000);
      setTimeout(() => setRefreshTrigger(prev => prev + 1), 1000);
    } catch (err) {
      console.error('Error ending trial:', err);
      setError('Failed to end trial: ' + (err instanceof Error ? err.message : 'Unknown error'));
    } finally {
      setActionInProgress(null);
    }
  };

  const handleRevokeSubscription = async (userId: string) => {
    if (!confirm('Are you sure you want to revoke this user\'s premium subscription? They will lose access immediately.')) {
      return;
    }
    
    try {
      setActionInProgress(userId);
      setError(null);
      await revokeSubscription(userId);
      
      setUsers(prevUsers => 
        prevUsers.map(user => 
          user.id === userId 
            ? { ...user, subscription_active: false, subscription_tier_name: 'Expired' }
            : user
        )
      );
      
      setMessage('Subscription revoked successfully');
      setTimeout(() => setMessage(null), 3000);
      setTimeout(() => setRefreshTrigger(prev => prev + 1), 1000);
    } catch (err) {
      console.error('Error revoking subscription:', err);
      setError('Failed to revoke subscription: ' + (err instanceof Error ? err.message : 'Unknown error'));
    } finally {
      setActionInProgress(null);
    }
  };

  const handleResetToTrial = async (user: User) => {
    if (!confirm('Reset this user back to their normal trial? This will remove any gifted premium status.')) {
      return;
    }
    
    try {
      setActionInProgress(user.id);
      setError(null);
      await resetToTrial(user.id, user.created_at);
      
      const daysLeft = getTrialDaysLeft(user);
      setUsers(prevUsers => 
        prevUsers.map(u => 
          u.id === user.id 
            ? { ...u, subscription_active: true, subscription_tier_name: `Trial (${daysLeft} days)` }
            : u
        )
      );
      
      setMessage('User reset to trial successfully');
      setTimeout(() => setMessage(null), 3000);
      setTimeout(() => setRefreshTrigger(prev => prev + 1), 1000);
    } catch (err) {
      console.error('Error resetting to trial:', err);
      setError('Failed to reset to trial: ' + (err instanceof Error ? err.message : 'Unknown error'));
    } finally {
      setActionInProgress(null);
    }
  };

  const handlePasswordReset = async (userId: string) => {
    if (!confirm('Are you sure you want to reset this user\'s password? They will receive an email with instructions.')) {
      return;
    }

    setActionLoading(userId);
    setError(null);
    setMessage(null);

    try {
      // Call admin password reset function
      const { error } = await supabase.rpc('admin_reset_password', {
        target_user_id: userId,
        send_email: true
      });

      if (error) {
        console.error('Admin password reset error:', error);
        throw error;
      }

      setMessage('Password reset email sent successfully');
      setTimeout(() => setMessage(null), 5000);
    } catch (error: any) {
      console.error('Password reset failed:', error);
      setError(error.message || 'Failed to send password reset email');
      setTimeout(() => setError(null), 5000);
    } finally {
      setActionLoading(null);
    }
  };

  if (!isAdmin) {
    return (
      <div className="p-8">
        <div className="bg-crimson/10 text-crimson p-6 rounded-lg">
          <div className="flex items-center mb-4">
            <AlertCircle className="h-6 w-6 mr-2" />
            <h3 className="text-lg font-semibold">Access Denied</h3>
          </div>
          <p>You do not have permission to access the admin dashboard.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-electric" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8">
        <div className="bg-crimson/10 text-crimson p-6 rounded-lg">
          <div className="flex items-center mb-4">
            <AlertCircle className="h-6 w-6 mr-2" />
            <h3 className="text-lg font-semibold">Error Loading Users</h3>
          </div>
          <p className="mb-4">{error}</p>
          <button 
            onClick={() => setRefreshTrigger(prev => prev + 1)}
            className="px-4 py-2 bg-crimson text-white rounded-lg hover:bg-crimson/90"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  const fetchUsers = async () => {
      try {
        setLoading(true);
        setError(null);

        const { data, error } = await supabase.rpc('get_admin_users');

        if (error) {
          console.error('RPC Error:', error);
          throw error;
        }

        setUsers(data || []);
      } catch (err) {
        console.error('Error fetching users:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch users');
      } finally {
        setLoading(false);
      }
    };

  return (
    <div className="p-4 pb-24 md:p-8 md:pb-8">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <Users className="h-6 w-6 text-midnight mr-2" />
          <h1 className="text-2xl font-bold text-midnight">User Management</h1>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow-sm border border-sand/20 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate">Total Users</p>
              <p className="text-2xl font-bold text-midnight">{stats.total}</p>
            </div>
            <Users className="h-8 w-8 text-electric/50" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-sand/20 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate">Active Trials</p>
              <p className="text-2xl font-bold text-blue-600">{stats.activeTrials}</p>
            </div>
            <Clock className="h-8 w-8 text-blue-400" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-sand/20 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate">Premium</p>
              <p className="text-2xl font-bold text-emerald">{stats.premiumUsers}</p>
            </div>
            <Crown className="h-8 w-8 text-gold" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-sand/20 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate">Expired</p>
              <p className="text-2xl font-bold text-crimson">{stats.expiredTrials}</p>
            </div>
            <UserX className="h-8 w-8 text-crimson/50" />
          </div>
        </div>
      </div>

      {/* Tab bar */}
      <div className="flex gap-1 mb-6 border-b border-sand/20">
        {([
          { id: 'users',    label: 'Users' },
          { id: 'leads',    label: 'Leads' },
          { id: 'ai_usage', label: 'AI Usage' },
          { id: 'content',  label: 'Content' },
          { id: 'series',   label: 'Sermon Series' },
        ] as const).map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab.id
                ? 'border-electric text-electric'
                : 'border-transparent text-slate hover:text-midnight dark:hover:text-soft-white'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Search and Filter */}
      {activeTab === 'users' && (<>
      <div className="bg-white rounded-lg shadow-sm border border-sand/20 p-4 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate" />
            <input
              type="text"
              placeholder="Search by name or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-sand/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-electric/20"
            />
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setStatusFilter('all')}
              className={`px-3 py-2 text-sm rounded-lg ${statusFilter === 'all' ? 'bg-electric text-white' : 'bg-sand/10 text-slate hover:bg-sand/20'}`}
            >
              All
            </button>
            <button
              onClick={() => setStatusFilter('trial')}
              className={`px-3 py-2 text-sm rounded-lg ${statusFilter === 'trial' ? 'bg-blue-600 text-white' : 'bg-sand/10 text-slate hover:bg-sand/20'}`}
            >
              Trial
            </button>
            <button
              onClick={() => setStatusFilter('premium')}
              className={`px-3 py-2 text-sm rounded-lg ${statusFilter === 'premium' ? 'bg-emerald text-white' : 'bg-sand/10 text-slate hover:bg-sand/20'}`}
            >
              Premium
            </button>
            <button
              onClick={() => setStatusFilter('expired')}
              className={`px-3 py-2 text-sm rounded-lg ${statusFilter === 'expired' ? 'bg-crimson text-white' : 'bg-sand/10 text-slate hover:bg-sand/20'}`}
            >
              Expired
            </button>
          </div>
        </div>
      </div>

      {message && (
        <div className="bg-emerald/10 text-emerald p-4 rounded-md mb-4 flex items-center">
          <CheckCircle className="h-4 w-4 mr-2" />
          {message}
        </div>
      )}

      {error && (
        <div className="bg-crimson/10 text-crimson p-4 rounded-md mb-4 flex items-center">
          <AlertCircle className="h-4 w-4 mr-2" />
          {error}
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm border border-sand/20 overflow-hidden">
        <div className="px-4 py-3 border-b border-sand/20 text-sm text-slate">
          Showing {filteredUsers.length} of {users.length} users
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-sand/10">
                <th className="px-6 py-3 text-left text-sm font-medium text-slate">User</th>
                <th className="px-6 py-3 text-left text-sm font-medium text-slate">Subscription</th>
                <th className="px-6 py-3 text-left text-sm font-medium text-slate">Created</th>
                <th className="px-6 py-3 text-left text-sm font-medium text-slate">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-sand/20">
              {filteredUsers.map(user => (
                <React.Fragment key={user.id}>
                  <tr className="hover:bg-sand/5">
                    <td className="px-6 py-4">
                      <button 
                        onClick={() => setSelectedUser(selectedUser === user.id ? null : user.id)}
                        className="flex items-center"
                      >
                        <div>
                          <div className="font-medium text-midnight">
                            {user.first_name && user.last_name ? 
                              `${user.first_name} ${user.last_name}` : 
                              user.email
                            }
                          </div>
                          <div className="text-sm text-slate">{user.email}</div>
                          <div className="flex items-center gap-1 mt-1">
                            {superAdminUsers.has(user.id) && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-crimson/10 text-crimson">
                                <Crown className="h-3 w-3 mr-1" />
                                Super Admin
                              </span>
                            )}
                            {adminUsers.has(user.id) && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-electric/10 text-electric">
                                <Shield className="h-3 w-3 mr-1" />
                                Admin
                              </span>
                            )}
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-sand/40 text-slate" title="Sermons created">
                              {user.sermon_count ?? 0} sermon{(user.sermon_count ?? 0) !== 1 ? 's' : ''}
                            </span>
                          </div>
                        </div>
                        {selectedUser === user.id ? (
                          <ChevronUp className="h-4 w-4 ml-2 text-slate" />
                        ) : (
                          <ChevronDown className="h-4 w-4 ml-2 text-slate" />
                        )}
                      </button>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center flex-wrap gap-1">
                        {getUserStatus(user) === 'premium' && (
                          <Crown className="h-4 w-4 text-gold mr-1" />
                        )}
                        <span className={`font-medium ${
                          user.subscription_tier_name === 'Collaborator' ? 'text-electric' :
                          getUserStatus(user) === 'premium' ? 'text-emerald' :
                          getUserStatus(user) === 'trial' ? 'text-blue-600' :
                          'text-crimson'
                        }`}>
                          {getStatusLabel(user)}
                        </span>
                      </div>
                      {isSuperAdmin && (
                        <div className="mt-2 flex flex-wrap gap-2">
                          {getUserStatus(user) !== 'premium' && (
                            <button
                              onClick={() => handleChangeSubscription(user.id, 'free_premium')}
                              disabled={actionInProgress === user.id}
                              className="px-2 py-1 text-xs rounded bg-emerald/10 text-emerald hover:bg-emerald/20 flex items-center"
                              title="Grant free premium access"
                            >
                              <UserCheck className="h-3 w-3 mr-1" />
                              Grant Premium
                            </button>
                          )}
                          {getUserStatus(user) === 'trial' && (
                            <button
                              onClick={() => handleEndTrial(user.id)}
                              disabled={actionInProgress === user.id}
                              className="px-2 py-1 text-xs rounded bg-crimson/10 text-crimson hover:bg-crimson/20 flex items-center"
                              title="End trial immediately"
                            >
                              <XCircle className="h-3 w-3 mr-1" />
                              End Trial
                            </button>
                          )}
                          {getUserStatus(user) === 'premium' && (
                            <>
                              {isTrialActive(user) && (
                                <button
                                  onClick={() => handleResetToTrial(user)}
                                  disabled={actionInProgress === user.id}
                                  className="px-2 py-1 text-xs rounded bg-blue-100 text-blue-700 hover:bg-blue-200 flex items-center"
                                  title="Reset to normal trial status"
                                >
                                  <RotateCcw className="h-3 w-3 mr-1" />
                                  Reset to Trial
                                </button>
                              )}
                              <button
                                onClick={() => handleRevokeSubscription(user.id)}
                                disabled={actionInProgress === user.id}
                                className="px-2 py-1 text-xs rounded bg-crimson/10 text-crimson hover:bg-crimson/20 flex items-center"
                                title="Revoke premium access"
                              >
                                <UserX className="h-3 w-3 mr-1" />
                                Revoke Premium
                              </button>
                            </>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 text-slate">
                      {new Date(user.created_at).toLocaleDateString()}
                      {user.subscription_expires_at && (
                        <div className="text-xs text-slate mt-1">
                          Expires: {new Date(user.subscription_expires_at).toLocaleDateString()}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-2">
                        {showTrialInput === user.id ? (
                          <div className="flex items-center space-x-2">
                            <input
                              type="number"
                              value={trialDays}
                              onChange={(e) => setTrialDays(parseInt(e.target.value) || 14)}
                              min="1"
                              max="365"
                              className="w-16 px-2 py-1 border border-sand rounded text-sm"
                            />
                            <button
                              onClick={() => handleExtendTrial(user.id, trialDays)}
                              disabled={actionInProgress === user.id}
                              className="p-1 text-emerald hover:text-emerald/80 rounded-lg"
                              title="Confirm"
                            >
                              <CheckCircle className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => setShowTrialInput(null)}
                              className="p-1 text-crimson hover:text-crimson/80 rounded-lg"
                              title="Cancel"
                            >
                              <XCircle className="h-4 w-4" />
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => setShowTrialInput(user.id)}
                            disabled={actionInProgress === user.id}
                            className="p-2 text-slate hover:text-electric rounded-lg flex items-center"
                            title="Extend Trial"
                          >
                            {actionInProgress === user.id ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <>
                                <Calendar className="h-4 w-4 mr-1" />
                                <span className="text-xs">Extend</span>
                              </>
                            )}
                          </button>
                        )}
                        <button
                          onClick={() => handlePasswordReset(user.id)}
                          className="p-2 text-slate hover:text-electric rounded-lg"
                          title="Reset Password"
                          disabled={actionLoading === user.id}
                        >
                          {actionLoading === user.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Key className="h-4 w-4" />
                          )}
                        </button>
                        {isSuperAdmin && (
                          <>
                            <button
                              onClick={() => handleToggleAdmin(
                                user.id,
                                adminUsers.has(user.id) || superAdminUsers.has(user.id),
                                false
                              )}
                              disabled={actionInProgress === user.id}
                              className="p-2 text-slate hover:text-electric rounded-lg"
                              title={adminUsers.has(user.id) ? "Revoke Admin" : "Make Admin"}
                            >
                              {actionInProgress === user.id ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : adminUsers.has(user.id) ? (
                                <ShieldOff className="h-4 w-4" />
                              ) : (
                                <Shield className="h-4 w-4" />
                              )}
                            </button>
                            <button
                              onClick={() => handleToggleAdmin(
                                user.id,
                                superAdminUsers.has(user.id),
                                true
                              )}
                              disabled={actionInProgress === user.id || user.id === (supabase.auth.getUser() as any)?.data?.user?.id}
                              className="p-2 text-slate hover:text-crimson rounded-lg"
                              title={superAdminUsers.has(user.id) ? "Revoke Super Admin" : "Make Super Admin"}
                            >
                              <Crown className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteUser(user.id, user.email)}
                              disabled={actionInProgress === user.id}
                              className="p-2 text-slate hover:text-crimson rounded-lg"
                              title="Delete User"
                            >
                              {actionInProgress === user.id ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <Trash2 className="h-4 w-4" />
                              )}
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                  {selectedUser === user.id && (
                    <tr className="bg-sand/5">
                      <td colSpan={4} className="px-6 py-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <h4 className="font-medium text-midnight mb-2">Contact Information</h4>
                            <div className="space-y-1 text-sm text-slate">
                              <div>Name: {user.first_name && user.last_name ? `${user.first_name} ${user.last_name}` : 'Not provided'}</div>
                              <div>Phone: {user.phone || 'Not provided'}</div>
                              <div>Email: {user.email}</div>
                              <div>Sermons: {user.sermon_count ?? 0}</div>
                            </div>
                          </div>
                          <div>
                            <h4 className="font-medium text-midnight mb-2">Subscription Details</h4>
                            <div className="space-y-1 text-sm text-slate">
                              <div>Status: {
                                isTrialActive(user) ? (
                                  <span className="text-blue-600">
                                    Trial ({getTrialDaysLeft(user)} days left)
                                  </span>
                                ) : isTrialExpired(user) ? (
                                  <span className="text-crimson">Trial Expired</span>
                                ) : user.subscription_tier_name === 'Trial' ? (
                                  <span className="text-gold">Trial</span>
                                ) : user.subscription_active ? (
                                  <span className="text-emerald">Active</span>
                                ) : (
                                  <span className="text-crimson">Inactive</span>
                                )
                              }</div>
                              <div>
                                Account Created: {new Date(user.created_at).toLocaleDateString()}
                                {isTrialActive(user) && (
                                  <span className="ml-2 text-xs bg-blue/10 text-blue-600 px-2 py-0.5 rounded-full">
                                    14-day trial
                                  </span>
                                )}
                              </div>
                              {user.subscription_expires_at && (
                                <div>
                                  Expires: {new Date(user.subscription_expires_at).toLocaleDateString()}
                                  {isTrialActive(user) && (
                                    <span className="ml-2 text-xs bg-gold/10 text-gold px-2 py-0.5 rounded-full">
                                      <Clock className="h-3 w-3 inline-block mr-1" />
                                      Premium Trial
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      </>)}

      {activeTab === 'leads' && (
      <div className="bg-white rounded-lg shadow-sm border border-sand/20 overflow-hidden">
        <div className="px-6 py-4 border-b border-sand/20 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Mail className="h-5 w-5 text-electric" />
            <div>
              <h3 className="font-semibold text-midnight">Study Guide Leads</h3>
              <p className="text-xs text-slate">{leads.length} lead{leads.length !== 1 ? 's' : ''} captured</p>
            </div>
          </div>
          <button
            onClick={downloadLeadsCSV}
            disabled={leads.length === 0 || leadsLoading}
            className="flex items-center gap-2 px-4 py-2 bg-electric text-white text-sm rounded-lg hover:bg-electric/90 disabled:opacity-50 transition-colors"
          >
            <Download className="h-4 w-4" />
            Download CSV
          </button>
        </div>
        {leadsLoading ? (
          <div className="p-8 text-center">
            <Loader2 className="h-6 w-6 animate-spin text-electric mx-auto" />
          </div>
        ) : leads.length === 0 ? (
          <div className="p-8 text-center text-slate text-sm">
            No leads captured yet. Leads will appear here when visitors use the free study guide generator.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-sand/10">
                  <th className="px-6 py-3 text-left text-sm font-medium text-slate">Email</th>
                  <th className="px-6 py-3 text-left text-sm font-medium text-slate">Name</th>
                  <th className="px-6 py-3 text-left text-sm font-medium text-slate">Source</th>
                  <th className="px-6 py-3 text-left text-sm font-medium text-slate">Converted</th>
                  <th className="px-6 py-3 text-left text-sm font-medium text-slate">Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-sand/20">
                {leads.map(lead => (
                  <tr key={lead.id} className="hover:bg-sand/5">
                    <td className="px-6 py-3 text-sm text-midnight">{lead.email}</td>
                    <td className="px-6 py-3 text-sm text-slate">{lead.first_name || '—'}</td>
                    <td className="px-6 py-3 text-sm text-slate">{lead.source || 'free-study-guide'}</td>
                    <td className="px-6 py-3">
                      {lead.converted ? (
                        <span className="inline-flex items-center gap-1 text-xs bg-emerald/10 text-emerald px-2 py-0.5 rounded-full">
                          <CheckCircle className="h-3 w-3" />
                          Yes
                        </span>
                      ) : (
                        <span className="text-xs bg-sand/20 text-slate px-2 py-0.5 rounded-full">No</span>
                      )}
                    </td>
                    <td className="px-6 py-3 text-sm text-slate">
                      {new Date(lead.created_at).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      )}

      {activeTab === 'ai_usage' && (
      <div className="bg-white rounded-lg shadow-sm border border-sand/20 overflow-hidden">
        <div className="px-6 py-4 border-b border-sand/20 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Activity className="h-5 w-5 text-electric" />
            <div>
              <h3 className="font-semibold text-midnight">AI Usage</h3>
              <p className="text-xs text-slate">Requests per user — click a row to see breakdown</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {[7, 30, 90].map(d => (
              <button
                key={d}
                onClick={() => {
                  setAiDaysBack(d);
                  setAiBreakdown({});
                  setExpandedAIUser(null);
                  loadAIUsage(d);
                }}
                className={`px-3 py-1 text-xs rounded-lg ${aiDaysBack === d ? 'bg-electric text-white' : 'bg-sand/10 text-slate hover:bg-sand/20'}`}
              >
                {d}d
              </button>
            ))}
            <button
              onClick={() => loadAIUsage(aiDaysBack)}
              className="p-1.5 text-slate hover:text-electric rounded-lg"
              title="Refresh"
            >
              <RefreshCw className="h-4 w-4" />
            </button>
          </div>
        </div>

        {aiUsage.length === 0 && !aiUsageLoading ? (
          <div className="p-8 text-center">
            <p className="text-slate text-sm mb-3">No AI usage data yet.</p>
            <button
              onClick={() => loadAIUsage(aiDaysBack)}
              className="px-4 py-2 bg-electric text-white text-sm rounded-lg hover:bg-electric/90"
            >
              Load Usage Data
            </button>
          </div>
        ) : aiUsageLoading ? (
          <div className="p-8 text-center">
            <Loader2 className="h-6 w-6 animate-spin text-electric mx-auto" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-sand/10">
                  <th className="px-6 py-3 text-left text-sm font-medium text-slate">User</th>
                  <th className="px-6 py-3 text-left text-sm font-medium text-slate">Total Requests</th>
                  <th className="px-6 py-3 text-left text-sm font-medium text-slate">OpenAI / Anthropic</th>
                  <th className="px-6 py-3 text-left text-sm font-medium text-slate">Last Active</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-sand/20">
                {aiUsage.map(row => (
                  <React.Fragment key={row.user_id}>
                    <tr
                      className="hover:bg-sand/5 cursor-pointer"
                      onClick={() => {
                        const next = expandedAIUser === row.user_id ? null : row.user_id;
                        setExpandedAIUser(next);
                        if (next) loadUserAIBreakdown(row.user_id, aiDaysBack);
                      }}
                    >
                      <td className="px-6 py-3">
                        <div className="flex items-center gap-2">
                          {expandedAIUser === row.user_id
                            ? <ChevronUp className="h-4 w-4 text-slate flex-shrink-0" />
                            : <ChevronDown className="h-4 w-4 text-slate flex-shrink-0" />}
                          <div>
                            <div className="text-sm font-medium text-midnight">
                              {row.first_name && row.last_name ? `${row.first_name} ${row.last_name}` : row.email}
                            </div>
                            <div className="text-xs text-slate">{row.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-3">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-semibold bg-electric/10 text-electric">
                          {row.total_requests}
                        </span>
                      </td>
                      <td className="px-6 py-3 text-sm text-slate">
                        {row.openai_requests > 0 && (
                          <span className="mr-3">
                            <span className="font-medium text-midnight">{row.openai_requests}</span> OpenAI
                          </span>
                        )}
                        {row.anthropic_requests > 0 && (
                          <span>
                            <span className="font-medium text-midnight">{row.anthropic_requests}</span> Anthropic
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-3 text-sm text-slate">
                        {new Date(row.last_request_at).toLocaleDateString()}
                      </td>
                    </tr>
                    {expandedAIUser === row.user_id && (
                      <tr className="bg-sand/5">
                        <td colSpan={4} className="px-10 py-3">
                          {aiBreakdown[row.user_id] ? (
                            <div className="flex flex-wrap gap-2">
                              {aiBreakdown[row.user_id].length === 0 ? (
                                <span className="text-sm text-slate">No breakdown available.</span>
                              ) : aiBreakdown[row.user_id].map(b => (
                                <span
                                  key={b.request_type}
                                  className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs bg-white border border-sand/30 text-slate"
                                >
                                  <span className="font-semibold text-midnight">{b.request_count}x</span>
                                  {b.request_type}
                                </span>
                              ))}
                            </div>
                          ) : (
                            <Loader2 className="h-4 w-4 animate-spin text-electric" />
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      )}

      {activeTab === 'content' && (
        <div className="space-y-8">
          <div className="bg-white dark:bg-charcoal rounded-lg shadow-sm border border-sand/20 dark:border-slate/20 p-6">
            <AnnouncementsManager />
          </div>
          <div className="bg-white dark:bg-charcoal rounded-lg shadow-sm border border-sand/20 dark:border-slate/20 p-6">
            <AdsManager />
          </div>
        </div>
      )}

      {activeTab === 'series' && (
        <div className="bg-white dark:bg-charcoal rounded-lg shadow-sm border border-sand/20 dark:border-slate/20 p-6">
          <SeriesManagerTab />
        </div>
      )}
    </div>
  );
}